import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import { Tooltip } from '@/components/ui/Tooltip'
import type { WeekStatus } from '@/lib/semester-types'
import { cn } from '@/lib/utils'
import { heeftNiveaus, maandgroepen, niveaufasen, niveaurangen } from '@/lib/weekgroepen'

const BLOK: Record<WeekStatus, string> = {
  les: 'bg-week-les',
  geen: 'bg-week-geen',
  vakantie: 'bg-week-vakantie week-arcering',
  inhaal: 'bg-week-inhaal/75',
}

export const WEEK_LABEL: Record<WeekStatus, string> = {
  les: 'Les',
  geen: 'Geen les',
  vakantie: 'Vakantie',
  inhaal: 'Inhaalweek',
}

/** Een oplopende reeks, zodat een latere fase zwaarder oogt dan een eerdere. */
const NIVEAU_BAND: Record<number, string> = {
  1: 'bg-niveau-1/14 ring-niveau-1/40',
  2: 'bg-niveau-2/20 ring-niveau-2/50',
  3: 'bg-niveau-3/24 ring-niveau-3/55',
  4: 'bg-niveau-4/28 ring-niveau-4/60',
}

const KOLOMBREEDTE = 16
const KOLOMGAT = 3

export interface StripWeek {
  maandag: string
  status: WeekStatus
  weeknummer?: number
  niveau?: string | null
  inVenster?: boolean
}

interface WeekstripProps {
  weken: StripWeek[]
  markering?: { van: string; tot: string }
  onToggle?: (maandag: string, inhaal: boolean) => void
  className?: string
}

function inVenster(week: StripWeek, markering?: { van: string; tot: string }): boolean {
  if (week.inVenster !== undefined) return week.inVenster
  if (!markering) return true
  return week.maandag >= markering.van && week.maandag <= markering.tot
}

/**
 * De scheidslijnen staan midden in de kolomkier voor en na het semestervenster, zodat
 * de grens zichtbaar blijft zonder dat de blokjes zelf van vorm veranderen.
 */
function vensterranden(weken: StripWeek[], markering?: { van: string; tot: string }): number[] {
  let eerste = -1
  let laatste = -1
  weken.forEach((week, index) => {
    if (!inVenster(week, markering)) return
    if (eerste === -1) eerste = index
    laatste = index
  })
  if (eerste === -1) return []

  const positie = (index: number) => index * (KOLOMBREEDTE + KOLOMGAT) - KOLOMGAT / 2
  const randen: number[] = []
  if (eerste > 0) randen.push(positie(eerste))
  if (laatste < weken.length - 1) randen.push(positie(laatste + 1))
  return randen
}

export function Weekstrip({ weken, markering, onToggle, className }: WeekstripProps) {
  const maanden = maandgroepen(weken)
  const fasen = heeftNiveaus(weken) ? niveaufasen(weken) : []
  const rangen = niveaurangen(fasen)
  const randen = vensterranden(weken, markering)

  return (
    <div className={cn('overflow-x-auto scrollbar-none', className)}>
      <div
        className="relative inline-grid gap-y-1.5"
        style={{
          gridTemplateColumns: `repeat(${weken.length}, ${KOLOMBREEDTE}px)`,
          columnGap: `${KOLOMGAT}px`,
        }}
      >
        {randen.map((links) => (
          <span
            key={links}
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 w-px bg-vensterrand"
            style={{ left: `${links}px` }}
          />
        ))}

        {weken.map((week) => {
          const actief = inVenster(week, markering)
          const weeknummer = week.weeknummer !== undefined ? `week ${week.weeknummer}, ` : ''
          const datum = format(parseISO(week.maandag), 'd MMMM', { locale: nl })
          const niveau = week.niveau ? `, niveau ${week.niveau}` : ''
          const titel = `${weeknummer}${datum}: ${WEEK_LABEL[week.status].toLowerCase()}${niveau}`
          const vorm = cn(
            'h-7 w-full rounded-[3px] ring-1 ring-inset ring-hairline transition-opacity duration-150',
            BLOK[week.status],
            actief ? 'opacity-100' : 'opacity-60',
          )

          if (!onToggle || week.status === 'vakantie') {
            return (
              <Tooltip key={week.maandag} content={titel}>
                <div className={vorm} aria-label={titel} />
              </Tooltip>
            )
          }

          return (
            <Tooltip
              key={week.maandag}
              content={`${titel}. Klik wisselt les en geen les, shift-klik zet een inhaalweek.`}
            >
              <button
                type="button"
                onClick={(gebeurtenis) => onToggle(week.maandag, gebeurtenis.shiftKey)}
                aria-label={titel}
                className={cn(
                  vorm,
                  'cursor-pointer hover:ring-hairline-strong focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primair-500/50',
                )}
              />
            </Tooltip>
          )
        })}

        {fasen.map((fase, index) =>
          fase.niveau === null ? (
            <span key={`fase-${index}`} style={{ gridColumn: `span ${fase.span}` }} />
          ) : (
            <Tooltip
              key={`fase-${index}`}
              content={`${fase.niveau}: ${fase.weken} ${fase.weken === 1 ? 'lesweek' : 'lesweken'}`}
            >
              <span
                style={{ gridColumn: `span ${fase.span}` }}
                className={cn(
                  'truncate rounded-[3px] px-1.5 py-0.5 text-center text-[10px] font-medium text-ink-soft ring-1 ring-inset',
                  NIVEAU_BAND[rangen.get(fase.niveau) ?? 1],
                )}
              >
                {fase.niveau} · {fase.weken} wk
              </span>
            </Tooltip>
          ),
        )}

        {maanden.map((maand, index) => (
          <span
            key={`${maand.label}-${index}`}
            className="label-caps truncate"
            style={{ gridColumn: `span ${maand.span}` }}
          >
            {maand.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export function Weeklegenda({ className }: { className?: string }) {
  const statussen: WeekStatus[] = ['les', 'geen', 'vakantie', 'inhaal']
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', className)}>
      {statussen.map((status) => (
        <span key={status} className="inline-flex items-center gap-1.5 text-xs text-ink-faint">
          <span
            className={cn('h-2.5 w-2.5 rounded-[2px] ring-1 ring-inset ring-hairline', BLOK[status])}
          />
          {WEEK_LABEL[status]}
        </span>
      ))}
    </div>
  )
}
